﻿/**
 * Singleton class which provides crosscutting functionality.
 *
 * # Properties
 * Name | Description
 * --- | ---
 * lookups | A backbone collection of Lookup models, loaded on app initialization.
 * routers | The array of router instances listening in the app.
 * windows | The array of windows (WindowViews) currently open that are linked to the app.
 * stylesheet | Gets and sets a string specifying a stylesheet to load for a page. For example, setting this property to `'stylesheet'` will load `'/assets/styles/app/stylesheet.css'`.
 * title | Gets and sets the title of the current page, which is both in the "title" tag and in the page header control.
 * heading | Gets and sets a heading to display in the info section of the current page.
 * message | Gets and sets text to display in the info section of the current page.
 * canScroll | A boolean indicating whether or not scrolling is enabled for the main scroll container of the app.
 * router | The router currently displaying a page.
 * modernizr | A reference to the modernizr library.
 * timestamp | The current UNIX time, in seconds from the epoch.
 * session | A reference to the Session for this application.
 * view | A reference to the most recently pushed Backbone.View, if any.
 * clipboard | A reference to an `app/Clipboard` instance.
 * @module app/App
 */

import $ from "jquery";
import PageInfoView from "core/views/PageInfoView";
import _ from "underscore";
import Backbone from "backbone";
import extend from "backbone-extend-standalone";
import * as ReactDOM from "react-dom";

var App = function () {
  _.extend(this, Backbone.Events);

  var self = this;

  Object.defineProperty(this, "title", {
    get: function () {
      return self.getPageTitle();
    },
    set: function (value) {
      self.setPageTitle(value);
    },
  });

  this.canScroll = true;
  this._pageInfo = this.createPageInfoView();

  this.setLayout(this.getDefaultLayout());
};

App.prototype = {
  /**
   * Initializes the application. This is called by the constructor.
   */
  initialize: function () {
    this._pageInfo.render();
  },

  /**
   * Generate the page info view for use by this instance.
   * @protected
   * @return {core/views/PageInfoView} An instance of PageInfoView.
   */
  createPageInfoView: function () {
    return new PageInfoView({
      el: $(".page-info-content"),
    });
  },

  /**
   * Gets the default layout name for this application.
   * @abstract
   * @type {Function}
   * @return {string} The layout name.
   */
  getDefaultLayout: function () {
    return null;
  },

  /**
   * Gets the title of the current page.
   * @return {String} The page title.
   */
  getPageTitle: function () {
    return $(".page-header h1").html() || this._title;
  },

  /**
   * Sets the title of the current page.
   * @param {String} value The page title.
   */
  setPageTitle: function (value) {
    var setServerSide = _.isUndefined(this._title);

    if (value === false && setServerSide === false) {
      $(".page-header-row").addClass("empty");
      $(".page-header h1").html("");
    } else if (!setServerSide) {
      $(".page-header-row").removeClass("empty");
      $(".page-header h1").html(value);
    }

    this._title = value;
    this.trigger("change:title", value);
  },

  /**
   * Disables scrolling in the main scroll container.
   * @abstract
   * @type {Function}
   */
  disableScroll: function () {
    return null;
  },

  /**
   * Enabled scrolling in the main scroll container.
   * @abstract
   * @type {Function}
   */
  enableScroll: function () {
    return null;
  },

  /**
   * Pushes the specified Backbone view to the content area and removes any previous view.
   * @param {Backbone.View} view The view to push.
   * @return {Backbone.View} A reference to the view that was pushed.
   */
  pushView: function (view) {
    if (this.view && this.view !== view) {
      this.view.remove();
    }

    this.view = view;
    this.resetLayout();

    return view;
  },

  /**
   * Reloads the page layout.
   * @abstract
   * @type {Function}
   * @return {Promise} A promise object which resolves when the layout is finally changed.
   */
  resetLayout: function () {},

  /**
   * Begins changing the overall page layout.
   * @param {string} name    The name of the new layout. This can be one of `'NoMenu'`, `'Wide'`, or `'WithMenu'`. The associated layout views are located in `assets/js/app/views/Layouts`.
   * @abstract
   * @type {Function}
   * @return {Promise} A promise object which resolves when the layout is finally changed.
   */
  setLayout: function (name) {},

  /**
   * Navigates back to the previous page.
   */
  back: function () {
    Backbone.history.history.back();
  },

  /**
   * Gets an array of router classes denoting the "core" routers.
   * @return {Array} An array of router module ids.
   * @abstract
   * @type {Function}
   */
  getCoreRouters: null,
};

App.extend = extend;

export default App;



//////////////////
// WEBPACK FOOTER
// ./public/js/core/base/App.js
// module id = 664
// module chunks = 1